import { bisector, extent } from 'd3-array';
import { scaleLinear, scalePoint } from 'd3-scale';
import { area as d3area, curveMonotoneX, line as d3line } from 'd3-shape';
import { useId, useMemo, useState, type PointerEvent } from 'react';
import type { Trend } from '@upbeat/shared';
import { chartColors, chartFont, chartLayout, lineStyle } from '../../../design/charts.js';
import { fmtInt, fmtValue, tickSpecifier } from '../lib/format';
import { Tooltip, useTooltip } from './Tooltip';
import { useMeasure } from './useMeasure';
import { t } from '../i18n';

interface Props { data: Trend; height?: number }
interface Series { region: string; label: string; points: { year: number; median: number; n: number }[] }

const M = { top: 16, right: 84, bottom: 36, left: 56 };

/**
 * One line per region. The design has a single accent, so one region is gold and the rest are
 * muted; hovering a line or the legend previews it, clicking the legend pins it.
 * A crosshair tooltip lists every region's median at the hovered year.
 */
export function TrendChart({ data, height = 420 }: Props) {
  const { ref, width } = useMeasure();
  const { tip, show, hide } = useTooltip();
  const gradId = useId();
  const [pinned, setPinned] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [year, setYear] = useState<number | null>(null);

  const series = useMemo<Series[]>(() => {
    const by = new Map<string, Series>();
    for (const r of data.rows) {
      const s = by.get(r.region) ?? { region: r.region, label: r.label, points: [] };
      s.points.push({ year: r.year, median: r.median, n: r.n });
      by.set(r.region, s);
    }
    return [...by.values()]; // API order = display order
  }, [data.rows]);

  const years = useMemo(() => [...new Set(data.rows.map((r) => r.year))].sort((a, b) => a - b), [data.rows]);
  const active = hover ?? pinned ?? series[0]?.region ?? null;

  const isMobile = width < 600;
  const marginRight = isMobile ? 24 : M.right;
  const innerW = Math.max(120, width - M.left - marginRight);
  const innerH = height - M.top - M.bottom;
  const x = scalePoint<number>().domain(years).range([0, innerW]);
  const [lo, hi] = extent(data.rows, (r) => r.median) as [number, number];
  // prototype: domain padded to [0.92·min, 1.06·max]; the baseline is not forced to zero
  const y = scaleLinear()
    .domain([lo >= 0 ? lo * chartLayout.yDomainPad.low : lo * 1.08, (hi || 1) * chartLayout.yDomainPad.high])
    .nice()
    .range([innerH, 0]);
  const yTicks = y.ticks(5);
  const yf = y.tickFormat(5, tickSpecifier(data.units));
  const every = width < 600 ? 3 : width < 900 ? 2 : 1;

  const path = d3line<Series['points'][number]>().x((p) => x(p.year) ?? 0).y((p) => y(p.median)).curve(curveMonotoneX);
  const areaPath = d3area<Series['points'][number]>().x((p) => x(p.year) ?? 0).y0(innerH).y1((p) => y(p.median)).curve(curveMonotoneX);
  const bis = bisector<number, number>((d) => d).center;

  const onMove = (e: PointerEvent<SVGRectElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - box.left;
    const xs = years.map((yr) => x(yr) ?? 0);
    const i = bis(xs, px);
    const yr = years[i];
    if (yr === undefined) return;
    setYear(yr);
    show(e, <YearTip year={yr} series={series} active={active} units={data.units} />);
  };
  const leave = () => { hide(); setYear(null); setHover(null); };

  const activeSeries = series.find((s) => s.region === active);
  const last = activeSeries?.points.at(-1);

  return (
    <>
      <div className="chart" ref={ref}>
        {width > 0 && (
          <svg width={width} height={height} role="img" aria-label={t.trendAria}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={lineStyle.areaGradient.color} stopOpacity={lineStyle.areaGradient.from} />
                <stop offset="100%" stopColor={lineStyle.areaGradient.color} stopOpacity={lineStyle.areaGradient.to} />
              </linearGradient>
            </defs>
            <g transform={`translate(${M.left},${M.top})`}>
              {yTicks.map((t) => (
                <g key={t} transform={`translate(0,${y(t)})`}>
                  <line x1={0} x2={innerW} stroke={chartColors.grid} />
                  <text x={-12} y={4} textAnchor="end" fill={chartColors.axisText} fontSize={chartFont.tick.size} fontFamily={chartFont.tick.family}>{yf(t)}</text>
                </g>
              ))}
              {years.map((yr, i) => (i % every === 0 || i === years.length - 1) && (
                <text key={yr} x={x(yr)} y={innerH + 24} textAnchor="middle" fill={chartColors.axisText} fontSize={chartFont.tick.size} fontFamily={chartFont.tick.family}>{yr}</text>
              ))}

              {activeSeries && <path d={areaPath(activeSeries.points) ?? ''} fill={`url(#${gradId})`} />}

              {series.filter((s) => s.region !== active).map((s) => (
                <path key={s.region} d={path(s.points) ?? ''} fill="none"
                      stroke={chartColors.muted} strokeWidth={lineStyle.muted.width} strokeLinecap="round" strokeLinejoin="round" />
              ))}
              {/* wide invisible strokes as hit targets for hover-to-highlight */}
              {series.map((s) => (
                <path key={`hit-${s.region}`} d={path(s.points) ?? ''} fill="none" stroke="transparent" strokeWidth={14}
                      style={{ cursor: 'pointer' }}
                      onPointerEnter={() => setHover(s.region)} onPointerLeave={() => setHover(null)}
                      onClick={() => setPinned(s.region)} />
              ))}
              {activeSeries && (
                <>
                  <path d={path(activeSeries.points) ?? ''} fill="none" stroke={chartColors.primary}
                        strokeWidth={lineStyle.primary.width} strokeLinecap="round" strokeLinejoin="round" />
                  {activeSeries.points.map((p) => (
                    <circle key={p.year} cx={x(p.year)} cy={y(p.median)} r={year === p.year ? lineStyle.marker.r : 3.5}
                            fill={lineStyle.marker.fill} stroke={lineStyle.marker.stroke} strokeWidth={lineStyle.marker.strokeWidth} />
                  ))}
                  {last && (
                    <text
                      x={isMobile ? (x(last.year) ?? 0) - 2 : (x(last.year) ?? 0) + 12}
                      y={isMobile ? (y(last.median) < 22 ? y(last.median) + 18 : y(last.median) - 10) : y(last.median) + 5}
                      textAnchor={isMobile ? 'end' : 'start'}
                      fill={chartColors.primaryText}
                      fontSize={chartFont.label.size}
                      fontWeight={chartFont.label.weight}
                      fontFamily={chartFont.label.family}
                    >
                      {fmtValue(data.units, last.median)}
                    </text>
                  )}
                </>
              )}

              {year !== null && <line x1={x(year)} x2={x(year)} y1={0} y2={innerH} stroke={chartColors.axisText} strokeDasharray="2 4" />}
              <line x1={0} x2={innerW} y1={innerH} y2={innerH} stroke={chartColors.axisText} />
              <rect x={0} y={0} width={innerW} height={innerH} fill="transparent" onPointerMove={onMove} onPointerLeave={leave} />
            </g>
          </svg>
        )}
        <Tooltip tip={tip} width={width} />
      </div>
      <div className="legend legend-keys" role="group" aria-label={t.regions}>
        {series.map((s) => {
          const on = s.region === active;
          return (
            <button key={s.region} type="button" className={`key${on ? ' on' : ''}${pinned === s.region ? ' pinned' : ''}`}
                    aria-pressed={pinned === s.region}
                    onPointerEnter={() => setHover(s.region)} onPointerLeave={() => setHover(null)}
                    onFocus={() => setHover(s.region)} onBlur={() => setHover(null)}
                    onClick={() => setPinned(pinned === s.region ? null : s.region)}
                    title={`${s.label} · ${t.clickToPin}`}>
              <i style={{ background: on ? chartColors.primary : chartColors.muted }} />{s.label}
            </button>
          );
        })}
      </div>
    </>
  );
}

function YearTip({ year, series, active, units }: { year: number; series: Series[]; active: string | null; units: string | null }) {
  const rows = series
    .map((s) => ({ s, p: s.points.find((p) => p.year === year) }))
    .filter((r) => r.p)
    .sort((a, b) => (b.p!.median - a.p!.median));
  return (
    <div>
      <div className="tip-l" style={{ marginBottom: 6 }}>{year}</div>
      {rows.map(({ s, p }) => (
        <div key={s.region} className={`tip-row${s.region === active ? ' on' : ''}`}>
          <i style={{ background: s.region === active ? chartColors.primary : chartColors.muted }} />
          <span className="tip-name">{s.label}</span>
          <span className="tip-num">{fmtValue(units, p!.median)}</span>
          <span className="tip-n">n={fmtInt(p!.n)}</span>
        </div>
      ))}
    </div>
  );
}

